import { randomUuid } from '../../utils/randomUuid';
import { serializeMessageForHistory } from '../../utils/ai/aiHistoryFormatters';
import { getAiChatErrorMessage } from '../../utils/error/aiChatErrorMessage';

/**
 * Extract inline_chips from assistant JSON text (mirrors edge parser sanitization).
 * @returns {Array<{ id: string, label_key: string, prompt: string, label_vars?: object }>}
 */
export function extractInlineChipsFromAssistantText(raw) {
  if (!raw || typeof raw !== 'string') return [];
  const trimmed = raw.trim();
  const fence = trimmed.match(/^```(?:json)?\s*([\s\S]*?)```/m);
  const jsonStr = fence ? fence[1].trim() : trimmed;
  let parsed;
  try {
    parsed = JSON.parse(jsonStr);
  } catch {
    return [];
  }
  const chips = parsed.inline_chips ?? parsed.chips;
  if (!Array.isArray(chips)) return [];
  const out = [];
  for (const c of chips) {
    if (!c || typeof c !== 'object') continue;
    const id = typeof c.id === 'string' ? c.id.trim() : '';
    const label_key = typeof c.label_key === 'string' ? c.label_key.trim() : '';
    const prompt = typeof c.prompt === 'string' ? c.prompt.trim() : '';
    if (!id || !label_key || !prompt) continue;
    const label_vars = c.label_vars && typeof c.label_vars === 'object' ? c.label_vars : undefined;
    out.push({ id, label_key, prompt, ...(label_vars ? { label_vars } : {}) });
    if (out.length >= 4) break;
  }
  return out;
}

export function pushAssistantMessage(setMessages, payload) {
  const {
    text, action, streamContext, transcript, inline_chips, flip,
  } = payload;
  const actionWithIdempotency = action
    ? { ...action, idempotency_key: action.idempotency_key || randomUuid() }
    : null;
  const aiMsg = {
    id: randomUuid(),
    role: 'assistant',
    text,
    action: actionWithIdempotency,
    actionStatus: actionWithIdempotency && actionWithIdempotency.type && actionWithIdempotency.type !== 'none' ? 'pending' : null,
    actionResult: null,
    streamContext: streamContext ?? null,
    createdAt: Date.now(),
    ...(inline_chips?.length ? { inline_chips } : {}),
    ...(transcript != null ? { transcript } : {}),
    ...(flip ? { _flipListRow: flip } : {}),
  };
  setMessages((prev) => [...prev, aiMsg]);
}

export function pushErrorAssistantMessage(setMessages, errorOrText, locale) {
  // Caller may pass a string (legacy) or an Error / { code, message } shape.
  // We always render via getAiChatErrorMessage so the user sees a localized,
  // code-specific message instead of "Request failed" or empty bubbles.
  const text = getAiChatErrorMessage(errorOrText, locale);
  setMessages((prev) => [...prev, {
    id: randomUuid(),
    role: 'assistant',
    text,
    isError: true,
    createdAt: Date.now(),
  }]);
}

export function findCustomerNameInMessages(messages, customerId) {
  if (!customerId || !messages?.length) return '';
  const list = [...messages].reverse().find((m) => m?.action?.type === 'customer_list' && m?.action?.rows?.length);
  const row = list?.action?.rows?.find((r) => r.customer_id === customerId);
  return row?.name || '';
}

/**
 * Build last 10 conversation turns for the assistant stream.
 *
 * Differences from the previous version:
 *   - Action-only assistant turns (no text, just an action) emit a presence
 *     marker so Claude sees the action happened.
 *   - Every lifecycle state (pending / processing / completed / failed /
 *     dismissed / cancelled) emits the right suffix — not just completed
 *     and cancelled.
 *   - Single source of truth: serializeMessageForHistory.
 */
export function buildAssistantHistoryForStream(messagesRef, locale) {
  const last10 = messagesRef.current.slice(-10);
  const out = [];
  for (const m of last10) {
    const serialized = serializeMessageForHistory(m, locale);
    if (serialized) out.push(serialized);
  }
  return out;
}

export function buildLineItemsFromPreFill(pf) {
  const lineItems = [];
  for (const s of pf.services || []) {
    if (s.service_id) lineItems.push({ type: 'service', catalog_id: s.service_id, name: s.name, quantity: s.quantity || 1 });
  }
  for (const c of pf.chemicals || []) {
    if (c.chemical_id) lineItems.push({ type: 'chemical', catalog_id: c.chemical_id, name: c.name, quantity: c.quantity || 1 });
  }
  for (const e of pf.extras || []) {
    lineItems.push({ type: 'extra', catalog_id: null, name: e.description || '', quantity: e.quantity || 1, unit_price: e.price || 0 });
  }
  return lineItems;
}
